/**
 * Same-origin client for the /api Pages Functions.
 *
 * Auth is an HttpOnly session cookie set by POST /api/auth/login (see
 * server/auth.js); scripts never see the token. When the API answers 401 the
 * session is over: AUTH_REQUIRED is dispatched on window and the AuthProvider
 * sends the user to the sign-in page.
 */

const BASE = (import.meta.env.VITE_API_BASE || "") + "/api";
export const AUTH_REQUIRED = "content-analytics:auth-required";

export class ApiError extends Error {
  constructor(message, { status = 0, kind = "error", retryable = false } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.kind = kind;
    this.retryable = retryable;
  }
}

async function toError(res) {
  let body = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON error body
  }
  const e = body?.error ?? {};
  const kind = e.kind || (res.status === 401 ? "auth" : res.status === 403 ? "forbidden" : "error");
  if (res.status === 401) window.dispatchEvent(new CustomEvent(AUTH_REQUIRED));
  return new ApiError(e.message || `Request failed (${res.status}).`, { status: res.status, kind, retryable: Boolean(e.retryable) });
}

async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { credentials: "same-origin", ...init, headers: { Accept: "application/json", ...(init.headers ?? {}) } });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new ApiError("Could not reach the analytics API.", { kind: "network", retryable: true });
  }
  if (!res.ok) throw await toError(res);
  return res;
}

export async function apiGet(path, { signal } = {}) {
  return (await request(path, { signal })).json();
}

export async function apiPost(path, body) {
  return (await request(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body ?? {}) })).json();
}

/** Download a file (CSV) from the API, via an object URL. */
export async function apiDownload(path, filename) {
  const res = await request(path);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
